/**
 * Hook for language-aware navigation
 * Ensures all navigation maintains current language context
 */

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { addLocaleToPath, getLocaleFromPath, removeLocaleFromPath } from "../utils/url-locales";

export const useLocalizedNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get current locale from URL
  const currentLocale = getLocaleFromPath(location.pathname);

  /**
   * Navigate to a path while maintaining current language context
   */
  const navigateLocalized = useCallback(
    (path: string, options?: { replace?: boolean }) => {
      const localizedPath = currentLocale ? addLocaleToPath(path, currentLocale) : path;
      console.log(
        `[LocalizedNav] Navigating to: ${localizedPath} (from ${path}, locale: ${currentLocale})`,
      );
      navigate(localizedPath, options);
    },
    [navigate, currentLocale],
  );

  /**
   * Get a localized href for Link components
   */
  const getLocalizedHref = useCallback(
    (path: string) => {
      return currentLocale ? addLocaleToPath(path, currentLocale) : path;
    },
    [currentLocale],
  );

  /**
   * Get current path without locale prefix
   */
  const getCurrentPath = useCallback(() => {
    return removeLocaleFromPath(location.pathname);
  }, [location.pathname]);

  return {
    currentLocale,
    navigateLocalized,
    getLocalizedHref,
    getCurrentPath,
  };
};
